import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { moduleApi, authApi } from '../../lib/api';
import {
    ArrowLeft, Save, Loader2, Mail, Lock, Unlock, FolderOpen, Folder,
    Plus, Upload, Download, Trash2, X, CheckCircle, ListChecks
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

const SYSTEM_FOLDER_ABSENCES = 'Absences autorisées';
const FALLBACK_FOLDER = 'Documents divers';

interface JobPost {
    id: string;
    title: string;
}

interface OnboardingStep { id: string; label: string; type: 'checkbox' | 'upload'; description: string; required: boolean; }
interface OnboardingTemplateOption { id: string; name: string; is_active: boolean; }
interface OnboardingAssignment {
    id: string;
    template_id: string;
    template_name: string;
    steps_snapshot: OnboardingStep[];
    progress: Record<string, { completed: boolean }>;
    status: 'in_progress' | 'completed';
    assigned_at: string;
}

interface EmployeeDoc {
    id: string;
    display_name: string;
    file_name: string;
    mime_type?: string;
    file_size?: number;
    folder_name?: string | null;
    absence_request_id?: string | null;
    created_at?: string;
}

interface EmployeeDetail {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    address?: string;
    position: string;
    job_post_title?: string;
    hire_date?: string | null;
    date_naissance?: string | null;
    salary?: number | string | null;
    is_account_active?: number;
    portal_password?: string | null;
    portal_password_temp?: number;
    portal_username?: string | null;
}

const toDateInputValue = (iso?: string | null) => (iso ? String(iso).slice(0, 10) : '');

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result as string;
        const parts = result.split(';base64,');
        resolve(parts.length > 1 ? parts[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const EmployeeProfilePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
    const [jobPosts, setJobPosts] = useState<JobPost[]>([]);
    const [documents, setDocuments] = useState<EmployeeDoc[]>([]);
    const [rhModuleActive, setRhModuleActive] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const [form, setForm] = useState({
        first_name: '', last_name: '', position: '', email: '', phone: '', address: '',
        hire_date: '', date_naissance: '', hourly_rate: ''
    });
    const [saving, setSaving] = useState(false);

    const [portalBusy, setPortalBusy] = useState(false);

    const [onboardingTemplates, setOnboardingTemplates] = useState<OnboardingTemplateOption[]>([]);
    const [onboardingAssignments, setOnboardingAssignments] = useState<OnboardingAssignment[]>([]);
    const [assignTemplateId, setAssignTemplateId] = useState('');
    const [assigning, setAssigning] = useState(false);

    const [userFolders, setUserFolders] = useState<string[]>([]);
    const [showNewFolder, setShowNewFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [uploadTargetFolder, setUploadTargetFolder] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadAll = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const [emp, posts, docs, modules, onbTemplates, onbAssignments] = await Promise.all([
                moduleApi.getEmploye(id),
                moduleApi.getJobPosts(),
                moduleApi.getEmployeeDocuments(id),
                authApi.getMyModules().catch(() => []),
                moduleApi.getOnboardingTemplates().catch(() => []),
                moduleApi.getEmployeeOnboarding(id).catch(() => [])
            ]);
            setEmployee(emp);
            setJobPosts(posts || []);
            setDocuments(Array.isArray(docs) ? docs : []);
            setOnboardingTemplates((Array.isArray(onbTemplates) ? onbTemplates : []).filter((t: OnboardingTemplateOption) => t.is_active));
            setOnboardingAssignments(Array.isArray(onbAssignments) ? onbAssignments : []);
            const activeNames = Array.isArray(modules) ? modules.filter((m: any) => m.is_active === 1).map((m: any) => m.module_name) : [];
            setRhModuleActive(activeNames.includes('portail_salarie'));
            setForm({
                first_name: emp.first_name || '',
                last_name: emp.last_name || '',
                position: emp.position || '',
                email: emp.email || '',
                phone: emp.phone || '',
                address: emp.address || '',
                hire_date: toDateInputValue(emp.hire_date),
                date_naissance: toDateInputValue(emp.date_naissance),
                hourly_rate: emp.salary != null && emp.salary !== '' ? String(emp.salary) : ''
            });
            const distinctUserFolders = Array.from(new Set(
                (Array.isArray(docs) ? docs : [])
                    .map((d: EmployeeDoc) => d.folder_name)
                    .filter((f): f is string => !!f && f !== SYSTEM_FOLDER_ABSENCES)
            ));
            setUserFolders(distinctUserFolders);
        } catch (e: any) {
            showToast(e?.message || 'Erreur de chargement de la fiche', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [id]);

    const handleClose = () => {
        navigate('/employes');
    };

    const saveBaseInfo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        if (!form.first_name.trim() || !form.last_name.trim() || !form.position) {
            showToast('Nom, prénom et poste sont obligatoires.', 'error');
            return;
        }
        setSaving(true);
        try {
            await moduleApi.updateEmploye(id, {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                position: form.position,
                email: form.email.trim(),
                phone: form.phone.trim(),
                address: form.address.trim(),
                hire_date: form.hire_date || null,
                date_naissance: form.date_naissance || null,
                salary: form.hourly_rate.trim() ? Math.max(0, Number(form.hourly_rate)) : null
            });
            showToast('Fiche enregistrée avec succès.');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors de l\'enregistrement.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const activatePortal = async () => {
        if (!id) return;
        setPortalBusy(true);
        try {
            await moduleApi.activateEmployeePortal(id);
            showToast('Accès activé, email envoyé !');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur', 'error');
        } finally {
            setPortalBusy(false);
        }
    };

    const togglePortal = async () => {
        if (!id || !employee) return;
        setPortalBusy(true);
        try {
            await moduleApi.toggleEmployeePortal(id, !employee.is_account_active);
            showToast(employee.is_account_active ? 'Compte suspendu' : 'Compte réactivé');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur', 'error');
        } finally {
            setPortalBusy(false);
        }
    };

    const assignOnboarding = async () => {
        if (!id || !assignTemplateId) return;
        setAssigning(true);
        try {
            await moduleApi.assignOnboarding(id, assignTemplateId);
            showToast('Parcours assigné.');
            setAssignTemplateId('');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors de l\'assignation.', 'error');
        } finally {
            setAssigning(false);
        }
    };

    const cancelOnboarding = async (assignmentId: string) => {
        if (!confirm('Annuler ce parcours d\'intégration ?')) return;
        try {
            await moduleApi.deleteOnboardingAssignment(assignmentId);
            showToast('Parcours annulé.');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors de l\'annulation.', 'error');
        }
    };

    const createFolder = async () => {
        if (!id || !newFolderName.trim()) return;
        setCreatingFolder(true);
        try {
            await moduleApi.createEmployeeDocumentFolder(id, newFolderName.trim());
            setUserFolders(prev => Array.from(new Set([...prev, newFolderName.trim()])));
            setUploadTargetFolder(newFolderName.trim());
            setNewFolderName('');
            setShowNewFolder(false);
        } catch (e: any) {
            showToast(e?.message || 'Impossible de créer ce dossier', 'error');
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleUploadClick = (folderName: string) => {
        setUploadTargetFolder(folderName);
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !id || !uploadTargetFolder) return;
        setUploading(true);
        try {
            const base64 = await fileToBase64(file);
            await moduleApi.uploadEmployeeDocument(id, {
                display_name: file.name,
                file_name: file.name,
                mime_type: file.type || 'application/octet-stream',
                base64,
                folder_name: uploadTargetFolder
            });
            showToast('Document ajouté.');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors de l\'upload', 'error');
        } finally {
            setUploading(false);
        }
    };

    const downloadDoc = async (doc: EmployeeDoc) => {
        if (!id) return;
        try {
            const full = await moduleApi.downloadEmployeeDocument(id, doc.id);
            const storageKey: string = full.storage_key || '';
            const base64 = storageKey.startsWith('base64:') ? storageKey.slice('base64:'.length) : storageKey;
            const link = document.createElement('a');
            link.href = `data:${full.mime_type || 'application/octet-stream'};base64,${base64}`;
            link.download = full.file_name || doc.file_name || 'document';
            link.click();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors du téléchargement', 'error');
        }
    };

    const deleteDoc = async (doc: EmployeeDoc) => {
        if (!id) return;
        if (!confirm(`Supprimer le document "${doc.display_name}" ?`)) return;
        try {
            await moduleApi.deleteEmployeeDocument(id, doc.id);
            showToast('Document supprimé.');
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors de la suppression', 'error');
        }
    };

    const moveDoc = async (doc: EmployeeDoc, targetFolder: string) => {
        if (!id || !targetFolder || targetFolder === doc.folder_name) return;
        try {
            await moduleApi.moveEmployeeDocument(id, doc.id, targetFolder);
            showToast(`Document déplacé vers "${targetFolder}".`);
            await loadAll();
        } catch (e: any) {
            showToast(e?.message || 'Erreur lors du déplacement', 'error');
        }
    };

    const groupedDocs = useMemo(() => {
        const absences = documents.filter(d => d.folder_name === SYSTEM_FOLDER_ABSENCES);
        const misc = documents.filter(d => !d.folder_name);
        const byFolder: Record<string, EmployeeDoc[]> = {};
        userFolders.forEach(f => { byFolder[f] = []; });
        documents.forEach(d => {
            if (d.folder_name && d.folder_name !== SYSTEM_FOLDER_ABSENCES) {
                byFolder[d.folder_name] = byFolder[d.folder_name] || [];
                byFolder[d.folder_name].push(d);
            }
        });
        return { absences, misc, byFolder };
    }, [documents, userFolders]);

    if (loading) {
        return (
            <div className="min-h-screen bg-surface-app flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-brand-600" />
            </div>
        );
    }

    if (!employee) {
        return (
            <div className="min-h-screen bg-surface-app flex items-center justify-center text-content-muted text-sm">
                Employé introuvable.
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app text-content p-4 md:p-6">
            <div className="max-w-6xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <button onClick={handleClose} className="flex items-center gap-1.5 text-content-muted hover:text-content text-xs font-semibold transition-all">
                        <ArrowLeft size={14} /> Retour
                    </button>
                    <h1 className="text-base font-black text-content">{employee.first_name} {employee.last_name}</h1>
                </div>

                {/* Section: Informations de base */}
                <form onSubmit={saveBaseInfo} className="space-y-3 pb-5 border-b border-border">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-content-muted">Informations</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Prénom *</label>
                            <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom *</label>
                            <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Poste *</label>
                            <select value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} className="w-full bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors">
                                <option value="">Sélectionner un poste</option>
                                {jobPosts.map(post => <option key={post.id} value={post.id}>{post.title}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse mail</label>
                            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse postale</label>
                            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Date de naissance</label>
                            <Input type="date" value={form.date_naissance} onChange={e => setForm({ ...form, date_naissance: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Date d'ancienneté</label>
                            <Input type="date" value={form.hire_date} onChange={e => setForm({ ...form, hire_date: e.target.value })} />
                            <p className="text-[11px] text-content-muted">Si renseignée, le salarié n'aura plus à la ressaisir dans ses demandes d'absence.</p>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Taux horaire (€/h)</label>
                            <Input type="number" min={0} step="0.01" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Ex: 12.50" />
                            <p className="text-[11px] text-content-muted">Utilisé pour calculer le coût du planning et les primes horaires. Jamais visible par le salarié lui-même.</p>
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Button type="submit" disabled={saving}>
                            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
                        </Button>
                    </div>
                </form>

                {/* Section: Compte portail salarié (gated par module RH) */}
                {rhModuleActive && (
                    <div className="space-y-3 pb-5 border-b border-border">
                        <h2 className="text-xs font-bold uppercase tracking-widest text-content-muted">Compte portail salarié</h2>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div>
                                {employee.is_account_active ? (
                                    <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded-md">
                                        Actif {employee.portal_password_temp ? '· MDP Temp' : ''}
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-black uppercase tracking-wider bg-surface border border-border text-content-muted px-2 py-1 rounded-md">
                                        {employee.portal_password ? 'Suspendu' : 'Jamais activé'}
                                    </span>
                                )}
                                {employee.portal_username && <span className="ml-2 text-xs text-brand-600 font-bold">Identifiant : {employee.portal_username}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                                {!employee.portal_password && (
                                    <Button size="sm" onClick={activatePortal} disabled={!employee.email || portalBusy}>
                                        <Mail size={12} /> Activer & Envoyer
                                    </Button>
                                )}
                                {employee.portal_password && (
                                    <Button variant="outline" size="sm" onClick={togglePortal} disabled={portalBusy} title={employee.is_account_active ? 'Suspendre' : 'Réactiver'} className="px-2!">
                                        {employee.is_account_active ? <Lock size={14} /> : <Unlock size={14} />}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {!employee.email && !employee.portal_password && (
                            <p className="text-xs text-red-400">Une adresse email est requise pour activer le portail.</p>
                        )}
                    </div>
                )}

                {/* Section: Parcours d'intégration */}
                <div className="space-y-3 pb-5 border-b border-border">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-content-muted flex items-center gap-2"><ListChecks size={14} /> Parcours d'intégration</h2>

                    {onboardingAssignments.length === 0 ? (
                        <p className="text-xs text-content-muted">Aucun parcours assigné pour l'instant.</p>
                    ) : (
                        <div className="space-y-3">
                            {onboardingAssignments.map((a) => {
                                const total = a.steps_snapshot.length;
                                const done = a.steps_snapshot.filter((s) => a.progress?.[s.id]?.completed).length;
                                return (
                                    <div key={a.id} className="border border-border rounded-md p-4 bg-surface space-y-2">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-sm font-bold text-content">{a.template_name}</p>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-md border ${a.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-warning-500/10 text-warning-400 border-warning-500/20'}`}>
                                                    {a.status === 'completed' ? 'Terminé' : `${done}/${total} étapes`}
                                                </span>
                                                <button onClick={() => cancelOnboarding(a.id)} title="Annuler ce parcours" className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={13} /></button>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {a.steps_snapshot.map((s) => (
                                                <div key={s.id} className="flex items-center gap-2 text-xs">
                                                    <CheckCircle size={12} className={a.progress?.[s.id]?.completed ? 'text-emerald-400' : 'text-content-muted opacity-30'} />
                                                    <span className={a.progress?.[s.id]?.completed ? 'text-content-muted line-through' : 'text-content'}>{s.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {onboardingTemplates.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t border-border">
                            <select value={assignTemplateId} onChange={(e) => setAssignTemplateId(e.target.value)} className="flex-1 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors">
                                <option value="">Assigner un parcours...</option>
                                {onboardingTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            <Button size="sm" onClick={assignOnboarding} disabled={!assignTemplateId || assigning} className="shrink-0">
                                {assigning ? <Loader2 size={14} className="animate-spin" /> : 'Assigner'}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Section: Documents par dossiers */}
                <div className="space-y-3 pb-2">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs font-bold uppercase tracking-widest text-content-muted">Documents</h2>
                        <Button variant="outline" size="sm" onClick={() => setShowNewFolder(true)} className="text-brand-600! border-brand-500/30! hover:bg-brand-500/10!">
                            <Plus size={14} /> Créer un dossier
                        </Button>
                    </div>

                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />

                    {showNewFolder && (
                        <div className="flex items-center gap-2 bg-surface border border-border rounded-md p-3">
                            <Input
                                autoFocus
                                value={newFolderName}
                                onChange={e => setNewFolderName(e.target.value)}
                                placeholder="Nom du dossier (ex: Contrats)"
                                className="flex-1"
                            />
                            <Button size="sm" onClick={createFolder} disabled={!newFolderName.trim() || creatingFolder}>
                                {creatingFolder ? <Loader2 size={14} className="animate-spin" /> : 'Créer'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="px-2!">
                                <X size={14} />
                            </Button>
                        </div>
                    )}

                    {/* Dossier système : Absences autorisées, toujours visible */}
                    <FolderBlock
                        icon={<FolderOpen size={16} className="text-emerald-400" />}
                        name={SYSTEM_FOLDER_ABSENCES}
                        docs={groupedDocs.absences}
                        canUpload={false}
                        onDownload={downloadDoc}
                        onDelete={deleteDoc}
                        onMove={moveDoc}
                        availableFolders={userFolders}
                        uploading={false}
                    />

                    {/* Dossiers créés par le client */}
                    {userFolders.map(folderName => (
                        <FolderBlock
                            key={folderName}
                            icon={<Folder size={16} className="text-brand-600 dark:text-brand-400" />}
                            name={folderName}
                            docs={groupedDocs.byFolder[folderName] || []}
                            canUpload
                            uploading={uploading && uploadTargetFolder === folderName}
                            onUpload={() => handleUploadClick(folderName)}
                            onDownload={downloadDoc}
                            onDelete={deleteDoc}
                            onMove={moveDoc}
                            availableFolders={[SYSTEM_FOLDER_ABSENCES, ...userFolders.filter(f => f !== folderName)]}
                        />
                    ))}

                    {groupedDocs.misc.length > 0 && (
                        <FolderBlock
                            icon={<Folder size={16} className="text-content-muted" />}
                            name={FALLBACK_FOLDER}
                            docs={groupedDocs.misc}
                            canUpload={false}
                            onDownload={downloadDoc}
                            onDelete={deleteDoc}
                            onMove={moveDoc}
                            availableFolders={[SYSTEM_FOLDER_ABSENCES, ...userFolders]}
                            uploading={false}
                        />
                    )}

                    {userFolders.length === 0 && (
                        <p className="text-xs text-content-muted">Créez un dossier pour pouvoir ajouter vos propres documents (contrats, diplômes...).</p>
                    )}
                </div>
            </div>

            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-2xl text-sm font-bold flex items-center gap-2 z-50 ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
                    >
                        {toast.type === 'success' && <CheckCircle size={16} />}
                        {toast.msg}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FolderBlock: React.FC<{
    icon: React.ReactNode;
    name: string;
    docs: EmployeeDoc[];
    canUpload: boolean;
    uploading: boolean;
    onUpload?: () => void;
    onDownload: (doc: EmployeeDoc) => void;
    onDelete?: (doc: EmployeeDoc) => void;
    onMove?: (doc: EmployeeDoc, targetFolder: string) => void;
    availableFolders?: string[];
}> = ({ icon, name, docs, canUpload, uploading, onUpload, onDownload, onDelete, onMove, availableFolders = [] }) => {
    return (
        <div className="border border-border rounded-md p-4 bg-surface space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-content">
                    {icon} {name} <span className="text-xs font-normal text-content-muted">({docs.length})</span>
                </div>
                {canUpload && onUpload && (
                    <button onClick={onUpload} disabled={uploading} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content bg-surface-soft hover:bg-surface px-3 py-1.5 rounded-md transition-all">
                        {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Ajouter un fichier
                    </button>
                )}
            </div>
            {docs.length === 0 ? (
                <p className="text-xs text-content-muted">Aucun document.</p>
            ) : (
                <div className="space-y-2">
                    {docs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between bg-surface-soft rounded-md px-3 py-2 gap-2">
                            <span className="text-xs text-content-muted truncate">{doc.display_name}</span>
                            <div className="flex items-center gap-1 shrink-0">
                                {onMove && availableFolders.length > 0 && (
                                    <select
                                        value=""
                                        onChange={e => { if (e.target.value) onMove(doc, e.target.value); }}
                                        title="Déplacer vers..."
                                        className="bg-surface border border-border rounded-md text-[11px] text-content-muted px-1.5 py-1 outline-none focus:border-brand-500/50 max-w-[110px]"
                                    >
                                        <option value="">Déplacer...</option>
                                        {availableFolders.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                )}
                                <button onClick={() => onDownload(doc)} title="Télécharger" className="p-1.5 rounded-md text-content-muted hover:text-content hover:bg-surface transition-all">
                                    <Download size={13} />
                                </button>
                                {onDelete && !doc.absence_request_id && (
                                    <button onClick={() => onDelete(doc)} title="Supprimer" className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 transition-all">
                                        <Trash2 size={13} />
                                    </button>
                                )}
                                {!!doc.absence_request_id && (
                                    <span title="Document officiel lié à une demande d'absence — non supprimable" className="p-1.5 text-content-muted">
                                        <Lock size={13} />
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
